import { useState, useRef, useEffect } from 'react';
import {
    crearEmpleado,
    actualizarEmpleado,
    obtenerEmpleados,
    eliminarEmpleado
} from '../../services/empleado/empleadoService';
import { obtenerConfiguracionDet } from '../../services/Configuracion/ConfiguracionDetService';
import { obtenerReporteIndividualPdf } from '../../services/empleado/empleadoReporteService';
import { useNominaConfig } from './hooks/useNominaConfig';
import { useTelefonos } from './hooks/useTelefonos';
import { useFamiliares } from './hooks/useFamiliares';
import { useCuentasBancarias } from './hooks/useCuentasBancarias';
import { useEducacion } from './hooks/useEducacion';
import { useLicencia } from './hooks/useLicencias';
import { useLibreta } from './hooks/useLibreta';
import { useHistorial } from './hooks/useHistorial';
import { useOtrosDocs } from './hooks/useOtrosDocs';
import { useFotos } from './hooks/useFotos';
import { useUbicaciones } from './hooks/useUbicaciones';
import { useExperiencia } from './hooks/useExperiencia';

import {
    obtenerDocumentosPorEmpleado,
    eliminarDocumento,
    guardarPensionDeEmpleado,
    recuperaDatosPuesto,
    resetearCodigoEmp
} from '../../services/empleado/empleadoDocumentoService';

const extraerId = (dato) => {
    if (!dato) return null;
    if (typeof dato === 'object' && dato.configuracionDetId) return dato.configuracionDetId;
    if (!isNaN(parseInt(dato))) return parseInt(dato);
    return dato;
};

const getDocId = (doc) => {
    if (!doc) return null;
    // Busca todas las variantes posibles de nombre para el ID
    return doc.id || doc.documentoId || doc.documento_id || doc.cod_unico || null;
};

export const useEmpleados = () => {
    const toast = useRef(null);

    const { configMap, loadingConfig } = useNominaConfig();
    const [isSaving, setIsSaving] = useState(false);

    // 1. DEFINICIONES INICIALES
    const emptyEmployee = {
        id: null,
        tipoDoc: null,
        nroIdentificador: '',
        nombre: '',
        apellido: '',
        nacionalidad: null,
        lugarNacimiento: null,
        estatura: 1.5,
        fechaIngreso: null,
        fechaFin: null,
        email: '',
        tipoSangre: null,
        pension: false,
        estadoCivil: null,
        ubicacionGPS: '',
        codigoUnico: '',
        fechaNacimientoEmp: null
    };
    const [formData, setFormData] = useState(emptyEmployee);

    const [pensionFile, setPensionFile] = useState(null);
    const [tienePensionDoc, setTienePensionDoc] = useState(false);
    const [pensionDocId, setPensionDocId] = useState(null);

    const [datosPuesto, setDatosPuesto] = useState({});

    const [listas, setListas] = useState({
        tiposDocumento: [],
        paises: [],
        tiposSangre: [],
        estadosCiviles: [],
        bancos: [],
        tiposCuenta: [],
        tiposFamilia: [],
        parentescos: [],
        tiposTelefono: [],

        categoriasLicencia: [],
        categoriasLibreta: [],
        nivelesEducacion: [],
        tiposHistorial: [],
        tiposOtrosDocs: [],
        tiposEstadoEmpleado: []
    });

    const phoneLogic = useTelefonos({
        empleadoId: formData.id || formData.empleadoId,
        toast
    });

    const familiaresLogic = useFamiliares({
        empleadoId: formData.id || formData.empleadoId,
        toast
    });

    const cuentasLogic = useCuentasBancarias({
        empleadoId: formData.id || formData.empleadoId,
        toast
    });

    const educacionLogic = useEducacion({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const licenciaLogic = useLicencia({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const libretaLogic = useLibreta({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const historialLogic = useHistorial({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const otrosDocsLogic = useOtrosDocs({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const fotosLogic = useFotos({
        empleadoId: formData.id || formData.empleadoId,
        toast,
        configMap,
        listas
    });

    const ubicacionLogic = useUbicaciones({
        empleadoId: formData.id || formData.empleadoId,
        toast
    });

    const experienciaLogic = useExperiencia({
        empleadoId: formData.id || formData.empleadoId,
        toast
    });

    // 2. ESTADOS
    const [view, setView] = useState('list');
    const [isEditMode, setIsEditMode] = useState(false);
    const [employeesList, setEmployeesList] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});

    const [generalLoading, setGeneralLoading] = useState(false);

    const [unidadMedida, setUnidadMedida] = useState('m');

    const [addConfigDialog, setAddConfigDialog] = useState(false);
    const [currentConfigCode, setCurrentConfigCode] = useState(null); // Guardará 'NOM-0000-0012', etc.

    const [loadingReporte, setLoadingReporte] = useState(false);

    // Modales
    const [modals, setModals] = useState({
        licencia: false,
        libreta: false,
        telefonos: false,
        educacion: false,
        cuentas: false,
        addCuenta: false,
        historial: false,
        familia: false,
        addFamilia: false,
        otrosDocs: false,
        mapa: false
    });

    // Sub-Modales CRUD

    const [submitted, setSubmitted] = useState(false);

    // CARGA INICIAL
    useEffect(() => {
        const init = async () => {
            if (loadingConfig || Object.keys(configMap).length === 0) return;

            await cargarCatalogos();
            await cargarListaEmpleados();
        };
        init();
    }, [loadingConfig, configMap]);

    const generarReporteExpediente = async (id) => {
        if (!id) {
            toast.current.show({
                severity: 'warn',
                summary: 'Atención',
                detail: 'Debe seleccionar un empleado.'
            });
            return;
        }

        try {
            setLoadingReporte(true);
            const blob = await obtenerReporteIndividualPdf(id);

            // Crear una URL temporal para el archivo
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));

            // Abrir en una pestaña nueva
            const nuevaPestana = window.open(url, '_blank');

            // Si el navegador bloqueó la pestaña, forzar descarga como respaldo
            if (!nuevaPestana) {
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Expediente_Empleado_${id}.pdf`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Reporte generado correctamente.'
            });
        } catch (error) {
            console.error('Error al descargar reporte:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo generar el reporte.'
            });
        } finally {
            setLoadingReporte(false);
        }
    };

    const cargarCatalogos = async () => {
        try {
            const [
                paises,
                docs,
                sangre,
                civil,
                bancos,
                tiposCta,
                tiposFam,
                parentescos,
                tiposTel,
                catLicencia,
                catLibreta,
                nivEducacion,
                tipHistorial,
                tipOtros,
                tiposEstado
            ] = await Promise.all([
                obtenerConfiguracionDet(configMap.PAISES),
                obtenerConfiguracionDet(configMap.TIPO_DOC),
                obtenerConfiguracionDet(configMap.TIPO_SANGRE),
                obtenerConfiguracionDet(configMap.ESTADO_CIVIL),
                obtenerConfiguracionDet(configMap.BANCOS),
                obtenerConfiguracionDet(configMap.TIPO_CUENTA),
                obtenerConfiguracionDet(configMap.TIPO_GRUPO_FAMILIAR),
                obtenerConfiguracionDet(configMap.PARENTESCO),
                obtenerConfiguracionDet(configMap.TIPO_TELEFONO),
                obtenerConfiguracionDet(configMap.CATEGORIA_LICENCIA),
                obtenerConfiguracionDet(configMap.CATEGORIA_LIBRETA),
                obtenerConfiguracionDet(configMap.NIVEL_EDUCACION),
                obtenerConfiguracionDet(configMap.TIPO_HISTORIAL),
                obtenerConfiguracionDet(configMap.TIPO_OTROS_DOCS),
                obtenerConfiguracionDet(configMap.ESTADO_EMPLEADO)
            ]);

            setListas({
                tiposDocumento: docs || [],
                tiposSangre: sangre || [],
                estadosCiviles: civil || [],
                bancos: bancos || [],
                tiposCuenta: tiposCta || [],
                tiposFamilia: tiposFam || [],
                parentescos: parentescos || [],
                tiposTelefono: tiposTel || [],
                paises: paises || [],
                lugares: paises || [],

                categoriasLicencia: catLicencia || [],
                categoriasLibreta: catLibreta || [],
                nivelesEducacion: nivEducacion || [],
                tiposHistorial: tipHistorial || [],
                tiposOtrosDocs: tipOtros || [],
                tiposEstadoEmpleado: tiposEstado || []
            });
        } catch (error) {
            console.error('Error catálogos:', error);
        }
    };

    const cargarListaEmpleados = async () => {
        try {
            const data = await obtenerEmpleados();
            // REPARACIÓN: Ordenar alfabéticamente por apellido o por ID fijo
            // para que la posición sea siempre la misma.
            const listaOrdenada = data.sort((a, b) => a.empleadoId - b.empleadoId);
            //console.log ('QQQQ00= EMPLEADOS: ', listaOrdenada)
            setEmployeesList(listaOrdenada);
        } catch (error) {
            console.error(error);
        }
    };

    // --- NAVEGACIÓN ---

    const goToCreate = () => {
        setFormData(emptyEmployee);
        phoneLogic.setPhones([]);

        licenciaLogic.setLicencias([]);
        libretaLogic.setLibretas([]);
        educacionLogic.setEducacionList([]);
        familiaresLogic.setFamiliares([]);
        historialLogic.setHistorialList([]);
        otrosDocsLogic.setOtrosDocsList([]);
        fotosLogic.setFotosList([]);

        phoneLogic.setPhones([]);
        setTienePensionDoc(false);
        setPensionFile(null);
        // ============================

        setUnidadMedida('m');
        setIsEditMode(false);
        setView('form');

        setUnidadMedida('m');
        setIsEditMode(false);
        setView('form');
    };

    const goToEdit = async (employeeRow) => {
        //console.log('DATOS EMPLEADO.: ', employeeRow);
        const getId = (val) => (val && typeof val === 'object' ? val.configuracionDetId : val);

        const convertirFecha = (f) => {
            if (!f) return null;
            if (Array.isArray(f)) return new Date(f[0], f[1] - 1, f[2]);
            if (typeof f === 'object' && f.year)
                return new Date(f.year, f.monthValue - 1, f.dayOfMonth);
            if (typeof f === 'string') return new Date(f); // Intenta parsear string
            return new Date(f);
        };

        const datosLimpios = {
            id: employeeRow.empleadoId,
            nroIdentificador: employeeRow.nroIdentificador,
            nombre: employeeRow.nombre,
            apellido: employeeRow.apellido,
            email: employeeRow.correo,
            estatura: employeeRow.estatura,
            pension: employeeRow.pensionAlimentaria ? 'SI' : 'NO',
            ubicacionGPS: employeeRow.ubicacionGps || '',
            tipoDoc: getId(employeeRow.tipoDocumentoConfigDet),
            nacionalidad: getId(employeeRow.nacionalidadConfigDet) || 1,
            lugarNacimiento: getId(employeeRow.lugarNacimientoConfigDet) || 1,
            tipoSangre: getId(employeeRow.tipoSangreConfigDet),
            estadoCivil: getId(employeeRow.estadoCivilConfigDet),
            fechaIngreso: convertirFecha(employeeRow.fechaIngreso),
            fechaFin: convertirFecha(employeeRow.fechaFin),
            estado: getId(employeeRow.estadoConfigDet) || 1,

            urlPension: null,
            pensionDocId: null,
            codigoUnico: employeeRow.codigoUnico,
            fechaNacimientoEmp: convertirFecha(employeeRow.fechaNacimiento),
        };

        setFormData(datosLimpios);
        setIsEditMode(true);
        setView('form');

        phoneLogic.setPhones([]);
        licenciaLogic.setLicencias([]);
        libretaLogic.setLibretas([]);
        educacionLogic.setEducacionList([]);
        historialLogic.setHistorialList([]);
        otrosDocsLogic.setOtrosDocsList([]);
        fotosLogic.setFotosList([]);
        familiaresLogic.setFamiliares([]);
        ubicacionLogic.setUbicaciones([]);
        experienciaLogic.setExperiencias([]);

        if (employeeRow.empleadoId) {
            setGeneralLoading(true);

            try {
                await Promise.all([
                    familiaresLogic.recargarFamiliares(employeeRow.empleadoId),
                    phoneLogic.recargarTelefonos(employeeRow.empleadoId),
                    cuentasLogic.recargarCuentas(employeeRow.empleadoId),
                    //educacionLogic.recargarEducacion(employeeRow.empleadoId),
                    educacionLogic.cargaEducacion(employeeRow.empleadoId),
                    //licenciaLogic.recargarLicencias(employeeRow.empleadoId),
                    licenciaLogic.cargaLicencias(employeeRow.empleadoId),
                    //libretaLogic.recargarLibretas(employeeRow.empleadoId),
                    libretaLogic.cargaLibretas(employeeRow.empleadoId),
                    //historialLogic.recargarHistorial(employeeRow.empleadoId),
                    historialLogic.cargaHistorial(employeeRow.empleadoId),
                    //otrosDocsLogic.recargarOtrosDocs(employeeRow.empleadoId),
                    otrosDocsLogic.cargaOtrosDocs(employeeRow.empleadoId),
                    fotosLogic.recargarFotos(employeeRow.empleadoId),
                    ubicacionLogic.recargarUbicaciones(employeeRow.empleadoId),
                    experienciaLogic.recargarExperiencias(employeeRow.empleadoId)
                ]);

                const docs = await obtenerDocumentosPorEmpleado(employeeRow.empleadoId);

                const docPension = docs.find(
                    (d) =>
                        d.tipoDocumentoId === 'NOM-0000-0015-0005' ||
                        (d.descripcion && d.descripcion.includes('Pensión'))
                );

                if (docPension) {
                    // CORRECCIÓN: Usamos directamente .id basándonos en tu imagen
                    const realId = docPension.id;
                    setTienePensionDoc(true);
                    setFormData((prev) => ({
                        ...prev,
                        // Usamos .url porque en la imagen se ve que esa propiedad existe
                        urlPension: docPension.url,
                        pensionDocId: realId // <--- Guardamos el 24 aquí
                    }));
                } else {
                    setTienePensionDoc(false);
                    setFormData((prev) => ({ ...prev, urlPension: null, pensionDocId: null }));
                }

                // Datos de cliente Proyecto y puesto
                const datosProy = await recuperaDatosPuesto(employeeRow.nroIdentificador);
                setDatosPuesto(datosProy);
            } catch (error) {
                console.error('Error cargando documentos:', error);
            } finally {
                setGeneralLoading(false);
            }
        } else {
            if (!employeeRow.empleadoId) {
                setFormData(datosLimpios);
            }
        }

        recargarDocumentos(employeeRow.empleadoId);

        setIsEditMode(true);
        setView('form');
    };

    const goBack = () => setView('list');
    const handleChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const toggleModal = (modalName, show = true) => {
        setModals((prev) => ({ ...prev, [modalName]: show }));
    };
    const handleAddConfig = (tipoConfiguracion) => {
        const mapaCodigos = {
            TIPO_DOCUMENTO: configMap.TIPO_DOC,
            BANCOS: configMap.BANCOS,
            TIPO_CUENTA: configMap.TIPO_CUENTA,
            TIPO_GRUPO_FAMILIAR: configMap.TIPO_GRUPO_FAMILIAR,
            PARENTESCO: configMap.PARENTESCO,
            PAISES: configMap.PAISES,
            TIPO_SANGRE: configMap.TIPO_SANGRE, //
            ESTADO_CIVIL: configMap.ESTADO_CIVIL, //
            TIPO_TELEFONO: configMap.TIPO_TELEFONO,
            CATEGORIA_LICENCIA: configMap.CATEGORIA_LICENCIA,
            CATEGORIA_LIBRETA: configMap.CATEGORIA_LIBRETA,
            CATEGORIA_EDUCACION: configMap.NIVEL_EDUCACION,
            TIPO_HISTORIAL: configMap.TIPO_HISTORIAL,
            TIPO_OTROS_DOCS: configMap.TIPO_OTROS_DOCS
        };

        const codigoReal = mapaCodigos[tipoConfiguracion];
        if (codigoReal) {
            setCurrentConfigCode(codigoReal);
            setAddConfigDialog(true);
        } else {
            toast.current.show({
                severity: 'warn',
                summary: 'Config',
                detail: `Código no definido para ${tipoConfiguracion}`
            });
        }
    };

    // 2. Función que se ejecuta tras guardar con éxito
    const onConfigSaved = async (codigoGuardado) => {
        try {
            // 1. Pedimos al backend la lista NUEVA y actualizada
            const nuevaLista = await obtenerConfiguracionDet(codigoGuardado);

            // 2. Actualizamos el estado 'listas' para que los Dropdowns se repinten
            setListas((prev) => {
                const newState = { ...prev };

                // MAPEO: Asignamos la nueva lista a la variable correcta según el código
                // Usa tus constantes configMap aquí

                if (codigoGuardado === configMap.TIPO_DOC) newState.tiposDocumento = nuevaLista;
                else if (codigoGuardado === configMap.PAISES)
                    newState.paises = nuevaLista; // Sirve para Nacionalidad y Lugar Nac.
                else if (codigoGuardado === configMap.TIPO_SANGRE)
                    newState.tiposSangre = nuevaLista;
                else if (codigoGuardado === configMap.ESTADO_CIVIL)
                    newState.estadosCiviles = nuevaLista;
                else if (codigoGuardado === configMap.BANCOS) newState.bancos = nuevaLista;
                else if (codigoGuardado === configMap.TIPO_CUENTA)
                    newState.tiposCuenta = nuevaLista;
                else if (codigoGuardado === configMap.TIPO_GRUPO_FAMILIAR)
                    newState.tiposFamilia = nuevaLista;
                else if (codigoGuardado === configMap.PARENTESCO) newState.parentescos = nuevaLista;
                else if (codigoGuardado === configMap.TIPO_TELEFONO)
                    newState.tiposTelefono = nuevaLista;
                else if (codigoGuardado === configMap.CATEGORIA_LICENCIA)
                    newState.categoriasLicencia = nuevaLista;
                else if (codigoGuardado === configMap.CATEGORIA_LIBRETA)
                    newState.categoriasLibreta = nuevaLista;
                else if (codigoGuardado === configMap.NIVEL_EDUCACION)
                    newState.nivelesEducacion = nuevaLista;
                else if (codigoGuardado === configMap.TIPO_HISTORIAL)
                    newState.tiposHistorial = nuevaLista;
                else if (codigoGuardado === configMap.TIPO_OTROS_DOCS)
                    newState.tiposOtrosDocs = nuevaLista;

                // ... Agrega aquí cualquier otro que falte (Licencias, etc.) ...

                return newState;
            });

            toast.current.show({
                severity: 'success',
                summary: 'Actualizado',
                detail: 'Lista desplegable actualizada.'
            });
        } catch (error) {
            console.error('Error recargando lista:', error);
        }
    };

    const recargarDocumentos = async (empleadoId) => {
        if (!empleadoId) return;

        if (Object.keys(configMap).length === 0) return;

        try {
            const docs = await obtenerDocumentosPorEmpleado(empleadoId);

            // ---------------------------------------------------------
            // A. PROCESAR PENSIÓN
            // ---------------------------------------------------------
            const configPension = listas.tiposOtrosDocs.find(
                (c) => c.codigo === configMap.FORM_PENSION
            );

            const ID_PENSION_NUMERICO = configPension ? configPension.configuracionDetId : null;

            const docPension = docs.find(
                (d) =>
                    (ID_PENSION_NUMERICO && d.tipoDocumentoId === ID_PENSION_NUMERICO) ||
                    d.tipoDocumentoId === 'NOM-0000-0015-0005' ||
                    (d.descripcion && d.descripcion.toUpperCase().includes('PENSIÓN')) ||
                    d.descripcion.toUpperCase().includes('PENSION')
            );

            if (docPension) {
                const realId = getDocId(docPension); // Helper que ya tienes
                setTienePensionDoc(true);
                setFormData((prev) => ({
                    ...prev,
                    urlPension: docPension.url || docPension.adjunto_url,
                    pensionDocId: realId
                }));
            } else {
                setTienePensionDoc(false);
                setFormData((prev) => ({ ...prev, urlPension: null, pensionDocId: null }));
            }
        } catch (error) {
            console.error('Error recargando documentos:', error);
        }
    };

    const handleUnitChange = (nuevaUnidad) => {
        const valorActual = formData.estatura;
        if (!valorActual) {
            setUnidadMedida(nuevaUnidad);
            return;
        }
        let nuevoValor = valorActual;
        if (unidadMedida === 'm' && nuevaUnidad === 'cm') nuevoValor = valorActual * 100;
        else if (unidadMedida === 'cm' && nuevaUnidad === 'm') nuevoValor = valorActual / 100;
        setFormData((prev) => ({ ...prev, estatura: parseFloat(nuevoValor.toFixed(2)) }));
        setUnidadMedida(nuevaUnidad);
    };

    const savePensionDoc = async (file) => {
        const empleadoId = formData.id || formData.empleadoId;
        if (!empleadoId) return;

        try {
            // 1. Si ya existe, borramos el anterior
            if (formData.pensionDocId) {
                await eliminarDocumento(formData.pensionDocId);
            }

            // 2. Subimos el archivo
            // Nota: Aunque la respuesta sea null o texto, el archivo ya está en la BD
            const respuesta = await guardarPensionDeEmpleado(empleadoId, {}, file);

            // 3. Intento de actualización rápida (Optimista)
            const realId = respuesta ? respuesta.id || respuesta.documentoId : null;

            if (realId) {
                setTienePensionDoc(true);
                setFormData((prev) => ({
                    ...prev,
                    pensionDocId: realId,
                    urlPension: respuesta.url
                }));
            }

            await recargarDocumentos(empleadoId);

            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Pensión guardada correctamente.'
            });
        } catch (e) {
            console.error('Error en flujo de pensión:', e);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo subir el archivo.'
            });
        }
    };

    const deletePensionDoc = async () => {
        if (!formData.pensionDocId) return;

        if (window.confirm('¿Desea eliminar el documento de pensión?')) {
            try {
                await eliminarDocumento(formData.pensionDocId);

                // --- LIMPIEZA VISUAL INMEDIATA ---
                setTienePensionDoc(false); // Esto cambia el badge a "SIN DOCUMENTO"
                setFormData((prev) => ({
                    ...prev,
                    pensionDocId: null,
                    urlPension: null
                }));
                toast.current.show({
                    severity: 'info',
                    summary: 'Eliminado',
                    detail: 'Documento removido.'
                });
            } catch (error) {
                console.error('Error al eliminar:', error);
            }
        }
    };

    const saveEmployee = async () => {
        if (isSaving) return;

        if (!formData.nroIdentificador || !formData.nombre || !formData.apellido) {
            toast.current.show({
                severity: 'warn',
                summary: 'Datos faltantes',
                detail: 'Identificación, nombre y apellido son obligatorios.'
            });
            return;
        }
        if (!formData.estado) {
            toast.current.show({
                severity: 'warn',
                summary: 'Datos faltantes',
                detail: 'Estado debe tener un valor.'
            });
            return;
        }

        // 2. Preparación de datos
        let estaturaFinal = formData.estatura;
        if (unidadMedida === 'cm' && estaturaFinal) estaturaFinal /= 100;

        // Formato YYYY-MM-DD para LocalDate de Java
        const formatDate = (d) => (d && d.toISOString ? d.toISOString().split('T')[0] : null);

        // --- CONSTRUCCIÓN DEL PAYLOAD (MAPEADO EXACTO AL DTO 'EmpleadoUpdateRequest') ---
        const payload = {
            // El DTO espera 'empleadoId' (Long)
            empleadoId: isEditMode ? formData.id || formData.empleadoId : null,

            nroIdentificador: formData.nroIdentificador,
            nombre: formData.nombre,
            apellido: formData.apellido,

            // El DTO espera 'correo' (String)
            correo: formData.email,

            estatura: parseFloat(estaturaFinal) || 0.0,

            // El DTO espera 'pensionAlimentaria' (Boolean)
            pensionAlimentaria: formData.pension === 'SI',

            // El DTO espera 'ubicacionGps' (ps minúscula)
            ubicacionGps: formData.ubicacionGPS || '0.0,0.0',

            fechaIngreso: formatDate(formData.fechaIngreso),
            fechaFin: formatDate(formData.fechaFin),

            // --- MAPEO DE IDs DE CONFIGURACIÓN (Uso de 'ConfigDetId' según tu DTO) ---
            // Aseguramos que sean números enteros (Integer)
            tipoDocumentoConfigDetId: parseInt(formData.tipoDoc),
            nacionalidadConfigDetId: parseInt(formData.nacionalidad || 1),
            lugarNacimientoConfigDetId: parseInt(formData.lugarNacimiento || 1),
            tipoSangreConfigDetId: parseInt(formData.tipoSangre),
            estadoCivilConfigDetId: parseInt(formData.estadoCivil),
            estadoConfigDetId: parseInt(formData.estado),
            fechaNacimiento: formatDate(formData.fechaNacimientoEmp)
        };

        try {
            setIsSaving(true);
            if (isEditMode) {
                // Se envía el ID y el objeto mapeado
                await actualizarEmpleado(payload.empleadoId, payload);
                toast.current.show({
                    severity: 'success',
                    summary: 'Actualizado',
                    detail: 'Datos modificados correctamente.'
                });
            } else {
                // Para crear se usa 'EmpleadoCreateRequest' (que tiene los mismos nombres de campo)
                await crearEmpleado(payload);
                toast.current.show({
                    severity: 'success',
                    summary: 'Guardado',
                    detail: 'Empleado creado correctamente.'
                });
            }

            await cargarListaEmpleados();
            setView('list');
        } catch (e) {
            console.error('Error capturado:', e);
            toast.current.show({
                severity: 'error',
                summary: 'Error al guardar',
                detail: 'El servidor rechazó los datos. Revise los logs del backend.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteEmployee = async (employee) => {
        // Usamos el confirm nativo por ahora para rapidez
        if (
            !window.confirm(`¿Estás seguro de eliminar a ${employee.nombre} ${employee.apellido}?`)
        ) {
            return;
        }

        try {
            // 1. Llamada al servicio con el ID del objeto de la tabla
            await eliminarEmpleado(employee.empleadoId);

            // 2. Notificación de éxito
            toast.current.show({
                severity: 'success',
                summary: 'Eliminado',
                detail: `Empleado ${employee.nombre} eliminado correctamente.`
            });

            // 3. Recargar la lista (esta función debe llamar a obtenerEmpleados() y setear el estado)
            await cargarListaEmpleados();
        } catch (error) {
            console.error('Error al eliminar empleado:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'No se pudo eliminar el empleado.'
            });
        }
    };

    const pausa = (ms) => new Promise((res) => setTimeout(res, ms));
    const resetCodigoUnico = async (rowData) => {
        try {
            const objData = {
                empleadoId: rowData.id,
                correo: rowData.email,
                pensionAlimentaria: true //
            };
            const data = await resetearCodigoEmp(objData);
            toast.current.show({
                severity: 'success',
                summary: 'Exito',
                detail: 'Se ha reseteado el Codigo'
            });
            await pausa(1000);
            await cargarListaEmpleados();
            setView('list');
        } catch (error) {
            console.error('Error Al Resetear Codigo Unico', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se ha podido Resetear Codigo Unico'
            });
        }
    };

    return {
        loadingConfig,
        toast,
        view,
        isEditMode,
        employeesList,
        formData,
        handleChange,
        unidadMedida,
        handleUnitChange,
        saveEmployee,
        deleteEmployee,
        handleAddConfig,
        modals,
        toggleModal,
        goToCreate,
        goToEdit,
        goBack,
        listas,

        validationErrors: validationErrors,
        generalLoading,

        accounts: cuentasLogic.accounts,
        openNewAccount: cuentasLogic.openNewAccount,
        openEditAccount: cuentasLogic.openEditAccount,
        saveAccount: cuentasLogic.saveAccount,
        deleteAccount: cuentasLogic.deleteAccount,
        accountData: cuentasLogic.accountData,
        setAccountData: cuentasLogic.setAccountData,
        handleSetPrincipalAccount: cuentasLogic.handleSetPrincipalAccount,
        accountDialog: cuentasLogic.accountDialog,
        setAccountDialog: cuentasLogic.setAccountDialog,
        accountLoading: cuentasLogic.loading,

        familiares: familiaresLogic.familiares,
        familiarData: familiaresLogic.familiarData,
        setFamiliarData: familiaresLogic.setFamiliarData,
        openNewFamiliar: familiaresLogic.openNewFamiliar,
        openEditFamiliar: familiaresLogic.openEditFamiliar,
        saveFamiliar: familiaresLogic.saveFamiliar,
        deleteFamiliar: familiaresLogic.deleteFamiliar,
        familyDialog: familiaresLogic.familyDialog,
        setFamilyDialog: familiaresLogic.setFamilyDialog,
        familiarLoading: familiaresLogic.loading,

        /* telefonos */
        phones: phoneLogic.phones,
        phoneData: phoneLogic.phoneData,
        setPhoneData: phoneLogic.setPhoneData,
        phoneDialog: phoneLogic.phoneDialog,
        setPhoneDialog: phoneLogic.setPhoneDialog,
        phoneSubmitted: phoneLogic.submitted, // Renombramos para evitar choques,
        openNewPhone: phoneLogic.openNewPhone,
        openEditPhone: phoneLogic.openEditPhone,
        savePhone: phoneLogic.savePhone,
        deletePhone: phoneLogic.deletePhone,
        handleSetPrincipalPhone: phoneLogic.handleSetPrincipalPhone,
        phoneLoading: phoneLogic.loading,

        // === LICENCIAS (HOOK) ===
        licencias: licenciaLogic.licencias,
        licenciaData: licenciaLogic.licenciaData,
        licenciaDialog: licenciaLogic.licenciaDialog,
        setLicenciaDialog: licenciaLogic.setLicenciaDialog,
        setLicenciaFile: licenciaLogic.setLicenciaFile,
        openNewLicencia: licenciaLogic.openNewLicencia,
        openEditLicencia: licenciaLogic.openEditLicencia,
        saveLicencia: licenciaLogic.saveLicencia,
        deleteLicencia: licenciaLogic.deleteLicencia,
        onLicenciaChange: licenciaLogic.onLicenciaChange,
        licenciaLoading: licenciaLogic.loading,
        guardaLicencia: licenciaLogic.guardaLicencia,
        anulaLicencia: licenciaLogic.anulaLicencia,

        // AGREGA ESTO AL FINAL DEL OBJETO RETURN: EDUCACION
        educacionList: educacionLogic.educacionList,
        educacionData: educacionLogic.educacionData,
        setEducacionData: educacionLogic.setEducacionData,
        educacionDialog: educacionLogic.educacionDialog,
        setEducacionDialog: educacionLogic.setEducacionDialog,
        educacionFile: educacionLogic.educacionFile,
        setEducacionFile: educacionLogic.setEducacionFile,
        openNewEducacion: educacionLogic.openNewEducacion,
        openEditEducacion: educacionLogic.openEditEducacion,
        saveEducacion: educacionLogic.saveEducacion,
        deleteEducacion: educacionLogic.deleteEducacion,
        educacionLoading: educacionLogic.loading,
        cargaEducacion: educacionLogic.cargaEducacion,
        guardaEducacion: educacionLogic.guardaEducacion,
        guardandoEdu: educacionLogic.guardandoEdu,
        anulaEducacion: educacionLogic.anulaEducacion,

        // === LIBRETAS (HOOK) ===
        libretas: libretaLogic.libretas,
        libretaData: libretaLogic.libretaData,
        libretaDialog: libretaLogic.libretaDialog, // Estado del formulario
        setLibretaDialog: libretaLogic.setLibretaDialog,
        setLibretaFile: libretaLogic.setLibretaFile,
        openNewLibreta: libretaLogic.openNewLibreta,
        openEditLibreta: libretaLogic.openEditLibreta,
        saveLibreta: libretaLogic.saveLibreta,
        deleteLibreta: libretaLogic.deleteLibreta,
        onLibretaChange: libretaLogic.onLibretaChange,
        libretaLoading: libretaLogic.loading,
        guardaLibreta: libretaLogic.guardaLibreta,
        procLibreta: libretaLogic.procLibreta,
        anulaLibreta: libretaLogic.anulaLibreta,

        // === HISTORIAL (HOOK) ===
        historialList: historialLogic.historialList,
        historialData: historialLogic.historialData,
        setHistorialData: historialLogic.setHistorialData,
        historialDialog: historialLogic.historialDialog, // Estado del modal form
        setHistorialDialog: historialLogic.setHistorialDialog, // Setter del modal form
        historialFile: historialLogic.historialFile,
        setHistorialFile: historialLogic.setHistorialFile,
        openNewHistorial: historialLogic.openNewHistorial,
        openEditHistorial: historialLogic.openEditHistorial,
        saveHistorial: historialLogic.saveHistorial,
        deleteHistorial: historialLogic.deleteHistorial,
        historialLoading: historialLogic.loading,
        guardaHistorial: historialLogic.guardaHistorial,
        grabaHistorial: historialLogic.grabaHistorial,
        anulaHistorial: historialLogic.anulaHistorial,

        // === OTROS DOCUMENTOS (HOOK) ===
        otrosDocsList: otrosDocsLogic.otrosDocsList,
        otrosDocsData: otrosDocsLogic.otrosDocsData,
        setOtrosDocsData: otrosDocsLogic.setOtrosDocsData,
        otrosDocsDialog: otrosDocsLogic.otrosDocsDialog, // Modal del formulario
        setOtrosDocsDialog: otrosDocsLogic.setOtrosDocsDialog,
        otrosDocsFile: otrosDocsLogic.otrosDocsFile,
        setOtrosDocsFile: otrosDocsLogic.setOtrosDocsFile,
        openNewOtrosDocs: otrosDocsLogic.openNewOtrosDocs,
        openEditOtrosDocs: otrosDocsLogic.openEditOtrosDocs,
        saveOtrosDocs: otrosDocsLogic.saveOtrosDocs,
        guardaOtrosDocs: otrosDocsLogic.guardaOtrosDocs,
        deleteOtrosDocs: otrosDocsLogic.deleteOtrosDocs,
        otrosDocsLoading: otrosDocsLogic.loading,
        procesaOtrosDocs: otrosDocsLogic.procesaOtrosDocs,
        anulaOtrosDocs: otrosDocsLogic.anulaOtrosDocs,

        //ubicaciones
        ubicacionesList: ubicacionLogic.ubicacionesList,
        ubicacionData: ubicacionLogic.ubicacionData,
        setUbicacionData: ubicacionLogic.setUbicacionData,
        ubicacionDialog: ubicacionLogic.ubicacionDialog,
        setUbicacionDialog: ubicacionLogic.setUbicacionDialog,
        openNewUbicacion: ubicacionLogic.openNewUbicacion,
        openEditUbicacion: ubicacionLogic.openEditUbicacion,
        saveUbicacion: ubicacionLogic.saveUbicacion,
        deleteUbicacion: ubicacionLogic.deleteUbicacion,
        handleSetPrincipalUbicacion: ubicacionLogic.handleSetPrincipalUbicacion,
        ubicacionLoading: ubicacionLogic.loading,
        updatingPrincipalId: ubicacionLogic.updatingPrincipalId,

        // === FOTOS (HOOK) ===
        fotosList: fotosLogic.fotosList,
        fotoData: fotosLogic.fotoData,
        setFotoData: fotosLogic.setFotoData,
        fotoFile: fotosLogic.fotoFile,
        setFotoFile: fotosLogic.setFotoFile,
        fotosDialog: fotosLogic.fotosDialog,
        setFotosDialog: fotosLogic.setFotosDialog,
        addFotoDialog: fotosLogic.addFotoDialog,
        setAddFotoDialog: fotosLogic.setAddFotoDialog,
        openGallery: fotosLogic.openGallery,
        openNewFoto: fotosLogic.openNewFoto,
        saveFoto: fotosLogic.saveFoto,
        deleteFoto: fotosLogic.deleteFoto,
        fotosLoading: fotosLogic.fotosLoading,

        // EXPERIENCIA LABORAL
        experiencias: experienciaLogic.experiencias,
        experienciaData: experienciaLogic.experienciaData,
        setExperienciaData: experienciaLogic.setExperienciaData,
        experienciaDialog: experienciaLogic.experienciaDialog,
        setExperienciaDialog: experienciaLogic.setExperienciaDialog,
        openNewExperiencia: experienciaLogic.openNewExperiencia,
        openEditExperiencia: experienciaLogic.openEditExperiencia,
        saveExperiencia: experienciaLogic.saveExperiencia,
        deleteExperiencia: experienciaLogic.deleteExperiencia,
        experienciaLoading: experienciaLogic.loading,

        generarReporteExpediente,
        loadingReporte,

        addConfigDialog,
        setAddConfigDialog,
        currentConfigCode,
        onConfigSaved,
        handleAddConfig,
        tienePensionDoc,
        savePensionDoc,
        deletePensionDoc,
        pensionFile,
        setPensionFile,
        datosPuesto,
        resetCodigoUnico
    };
};
